import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Button } from './components/ui/button';
import { Label } from './components/ui/label';
import { Toaster } from './components/ui/sonner';
import {
  Shield, Lock, Mail, Eye, EyeOff, AlertCircle, Heart, WifiOff, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import adminService from './services/adminService';
import { ADMIN_CONFIG } from './config';

interface AdminLoginProps {
  onLoginSuccess: () => void;
}

export function AdminLogin({ onLoginSuccess }: AdminLoginProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await adminService.login({ email, password });
      toast.success('Login successful');
      onLoginSuccess();
    } catch (err: any) {
      const status = err?.response?.status || err?.status;
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Login failed';

      if (status === 401 || status === 403) {
        setError('Invalid email or password. Please check your credentials.');
      } else if (!status || err?.code === 'ERR_NETWORK' || status >= 500) {
        // Report the URL actually used by the axios client. In dev that is the
        // Vite proxy ('/api' on this origin); in prod it is the baked VITE_API_URL.
        const apiTarget = import.meta.env.DEV
          ? `${window.location.origin}/api (proxied to the backend)`
          : import.meta.env.VITE_API_URL || ADMIN_CONFIG.apiBase;
        setError(
          `Cannot reach the API server at ${apiTarget}. ` +
          'Make sure the backend is running and VITE_API_URL is set correctly in .env',
        );
      } else {
        setError(msg);
      }

      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-blue-900 flex items-center justify-center p-4">
      <Toaster richColors />

      {/* Background Pattern */}
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: `radial-gradient(circle at 20% 50%, rgba(120,0,255,0.3) 0%, transparent 50%),
                            radial-gradient(circle at 80% 20%, rgba(0,100,255,0.3) 0%, transparent 50%)`,
        }}
      />

      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center gap-2 mb-4 bg-white rounded-2xl px-6 py-3 shadow-xl">
            <Heart className="size-8 text-pink-500 fill-pink-500" />
            <div className="text-2xl font-bold">
              <span className="text-blue-600">Dulha</span>
              <span className="text-pink-500">Dulhun</span>
            </div>
          </div>
          <h1 className="text-4xl font-bold text-white mb-2">Admin Portal</h1>
          <p className="text-purple-200">DulhaDulhun Management Console</p>
        </div>

        {/* Login Card */}
        <Card className="shadow-2xl border-0 overflow-hidden">
          <div className="h-2 bg-gradient-to-r from-purple-600 via-blue-600 to-purple-600" />

          <CardHeader className="pb-4">
            <CardTitle className="text-2xl text-center">Administrator Login</CardTitle>
            <p className="text-sm text-center text-gray-500 mt-1">
              Enter your admin credentials to access the dashboard
            </p>
          </CardHeader>

          <CardContent className="pt-0">
            <form onSubmit={handleLogin} className="space-y-5">
              {/* Error Message */}
              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                  {error.includes('reach') || error.includes('server') ? (
                    <WifiOff className="size-5 text-red-600 shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle className="size-5 text-red-600 shrink-0 mt-0.5" />
                  )}
                  <p className="text-sm text-red-800 flex-1">{error}</p>
                </div>
              )}

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-700 font-medium">
                  Email Address
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="admin@dulhadulhun.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className="pl-11 h-12"
                    required
                    disabled={isLoading}
                    autoComplete="username"
                  />
                </div>
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-700 font-medium">
                  Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-gray-400" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="pl-11 pr-11 h-12"
                    required
                    disabled={isLoading}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
                  </button>
                </div>
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-12 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold border-0"
              >
                {isLoading ? (
                  <>
                    <RefreshCw className="size-5 mr-2 animate-spin" />
                    Signing in…
                  </>
                ) : (
                  <>
                    <Shield className="size-5 mr-2" />
                    Sign In to Admin Panel
                  </>
                )}
              </Button>
            </form>

            <div className="mt-5 pt-5 border-t text-center">
              <a href="/" className="text-xs text-gray-400 hover:text-gray-600">
                ← Back to DulhaDulhun App
              </a>
            </div>
          </CardContent>
        </Card>

        <div className="mt-6 text-center text-sm text-purple-200">
          <p>© 2026 DulhaDulhun. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
